import {
  Component, ViewChild, NgZone, OnDestroy,
  Input, Output, EventEmitter, ChangeDetectorRef,
} from '@angular/core';
import { Observable } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import * as _ from 'lodash';
import Bugsnag from '@bugsnag/js';

import { SubscriptionService } from '../../subscription/subscription.service';
import { ChangePlanModalComponent } from '../../modal/change-plan-modal/change-plan-modal.component';
import { BillingInfoModalComponent } from '../../modal/billing-info-modal/billing-info-modal.component';
import { ManageBillingModalComponent } from '../../modal/manage-billing-modal/manage-billing-modal.component';
import { SendBillingModalComponent } from '../../modal/send-billing-modal/send-billing-modal.component';
import { PlanWarningModalComponent } from '../../modal/plan-warning-modal/plan-warning-modal.component';
import { ErrorModalComponent } from 'shared/modal/error-modal/error-modal.component';
import { BrandInfoService } from '../../brand/brand-info.service';
import { TermsService } from '../../terms/terms.service';
import { invoiceSentToast } from '../../toaster/toasts';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';
import {takeWhile} from 'rxjs/operators';
import { Config } from 'shared/config/env.config';


@Component({
  moduleId: module.id,
  selector: 'subscription-widget',
  templateUrl: 'subscription-widget.component.html',
})
export class SubscriptionWidgetComponent implements OnDestroy {

  planInfo: any;
  email: string;
  couponInfo: any;
  discountInfo: any;
  initialUsersCount: number;

  // for card info modal
  cardInfo: any;
  billingDate: string;

  isActive: boolean = true;

  updateCard: boolean = false;

  // https://speedlab.atlassian.net/browse/SPD2-2856
  ignoreSubscrValid: boolean = false;

  errorText: string = '';

  @Input() brandId: string;
  @Input() isAdmin: boolean = false;
  @Input() isManageBillingFlow: boolean = false;

  @Output() subscrUpdated = new EventEmitter();
  @Output() cardUpdated = new EventEmitter();

  @ViewChild(ChangePlanModalComponent, {static: false}) changePlanModal: ChangePlanModalComponent | void;
  @ViewChild(BillingInfoModalComponent, {static: false}) billingInfoModal: BillingInfoModalComponent | void;
  @ViewChild(ManageBillingModalComponent, {static: false}) manageBillingModal: ManageBillingModalComponent | void;
  @ViewChild(SendBillingModalComponent, {static: false}) sendBillingModal: SendBillingModalComponent | void;
  @ViewChild(PlanWarningModalComponent, {static: false}) planWarningModal: PlanWarningModalComponent | void;
  @ViewChild(ErrorModalComponent, {static: false}) private errorModal: ErrorModalComponent | void;

  constructor(
    protected ref: ChangeDetectorRef,
    private subscriptionService: SubscriptionService,
    private brandInfoService: BrandInfoService,
    private termsService: TermsService,
    private _zone: NgZone,
    private toastrService: ToastrService,
  ) { }

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

   get hasCard(): boolean {
     return _.has(this.cardInfo, 'card_last_four');
   }

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

   ngOnDestroy() {
     this.isActive = false;
   }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

   public openPlansModal() {
     this.subscriptionService.getSubscription(this.brandId)
      .subscribe((subscription: any) => {
        if (this.isValidSubscription(subscription)) { // subscription is valid - require discounts
          this.initialUsersCount = 0;
          this.subscriptionService.getDiscount(this.brandId)
            .subscribe((discountInfo: any) => {
              this.discountInfo = discountInfo;
              if (this.changePlanModal instanceof ChangePlanModalComponent) {
                this.changePlanModal.show();
              }
            });
        } else {
          forkJoin(
            this.subscriptionService.getCoupon(this.brandId),
            this.subscriptionService.getAccountBrand(this.brandId),
          )
            .pipe(takeWhile(() => this.isActive))
            .subscribe((res: any) => {
              const couponInfo = res[0];
              const accountBrand = res[1];

              this.discountInfo = couponInfo;
              this.initialUsersCount = _.get(accountBrand, 'initial_allowed_users', 0);
              if (this.changePlanModal instanceof ChangePlanModalComponent) {
                this.changePlanModal.show();
              }
            });
        }
      });

   }

   public openBillingModal() {
     this.setPlanId();
     this.subscriptionService.getSubscription(this.brandId)
       .pipe(takeWhile(() => this.isActive))
       .subscribe((subscription: any) => {
         const requests = [
           this.subscriptionService.getAccountBrand(this.brandId),
         ];
         if (!this.isValidSubscription(subscription)) {
           requests.push(this.subscriptionService.getCoupon(this.brandId));
         }

         forkJoin(requests)
          .pipe(takeWhile(() => this.isActive))
          .subscribe((res: any) => {
            const accountBrand = res[0];
            if (res[1]) {
              this.couponInfo = res[1];
            }

            if (_.get(accountBrand, 'card_last_four')) { // if we have already card - show modal with info
              this.cardInfo = accountBrand;
              this.billingDate = subscription.ends_at ? subscription.ends_at : subscription.trial_ends_at;
              if (this.billingInfoModal instanceof BillingInfoModalComponent) {
                this.billingInfoModal.show();
              }
            } else { // if not - show modal to fill card info
              if (this.manageBillingModal instanceof ManageBillingModalComponent) {
                this.manageBillingModal.show();
              }
            }
            this.ref.markForCheck();
          });
      });
   }

   public openStripeBillingUrl() {
     this.subscriptionService.getStripeManageBillingUrl(this.brandId)
       .subscribe((urlInfo: {
         message: string,
         url: string
       }) => {
         if (urlInfo.url) {
           window.open(urlInfo.url, '_blank');
         }
       });
   }

   public openManageBillingModal(updateCard?: boolean) {
     this.updateCard = !!updateCard;
     if (this.manageBillingModal instanceof ManageBillingModalComponent) {
       this.manageBillingModal.show();
     }
   }

   public openPlanWarningUsersModal() {
     if (this.planWarningModal) {
       this.planWarningModal.isAddUser = true;
       this.planWarningModal.isAddProducts = false;
       this.planWarningModal.isAdmin = this.isAdmin;
       this.planWarningModal.show();
     }
     this.ref.markForCheck();
   }

   public openPlanWarningProductsModal() {
     if (this.planWarningModal) {
       this.planWarningModal.isAddUser = false;
       this.planWarningModal.isAddProducts = true;
       this.planWarningModal.isAdmin = this.isAdmin;
       this.planWarningModal.show();
     }
     this.ref.markForCheck();
   }

   // https://speedlab.atlassian.net/browse/SPD2-2856
   public startFlow(ignoreSubscrValid?: boolean) {
     if (Config.BUGSNAG_KEY) {
       const prevPages = sessionStorage.getItem('previous_page_loads');
       const storedAccountBrand = localStorage.getItem('accountBrand');
       const storedSubscription = localStorage.getItem('subscription');
       const storedTerms = localStorage.getItem('terms');
       const userInfo = localStorage.getItem('userInfo');

       Bugsnag.notify(new Error('Payment popup'), (event) => {
         event.addMetadata('account_brand', JSON.parse(storedAccountBrand));
         event.addMetadata('subscription', 'subscription', storedSubscription); // add it even if null
         event.addMetadata('terms', JSON.parse(storedTerms));
         event.addMetadata('prev_pages', JSON.parse(prevPages));
         event.addMetadata('user_info', JSON.parse(userInfo));
       });
     }

     this.ignoreSubscrValid = !!ignoreSubscrValid;
     if (ignoreSubscrValid) {
       this.openPlansModal();
     } else {
       this.subscriptionService.getSubscription(this.brandId)
         .pipe(takeWhile(() => this.isActive))
         .subscribe((subscription: any) => {
           if (subscription.valid) {
             this.openStripeBillingUrl();
           } else {
             this.openPlansModal();
           }
         });
     }
     this.ref.markForCheck();
   }

   public checkTerms() {
     this.termsService.hasAcceptedLatest()
       .pipe(takeWhile(() => this.isActive))
       .subscribe((terms: any) => {
         if (terms.needs_acceptance) {
           this.termsService.callAccept();
         }
       });
   }

   public openSendBillingModal() {
     if (this.sendBillingModal instanceof SendBillingModalComponent) {
       this.sendBillingModal.show();
     }
   }

   /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

   onUpgradePlanClicked(planInfo: any) {
     if (this.changePlanModal instanceof ChangePlanModalComponent) {
       this.changePlanModal.showLoader();
     }

     // check if subscription valid
     this.subscriptionService.getSubscription(this.brandId)
       .pipe(takeWhile(() => this.isActive))
       .subscribe((subscription) => {
         if (subscription.valid && !this.ignoreSubscrValid) { // subscription is valid - just update current plan
           this.updateSubscription(
             null,
             planInfo.planId,
             planInfo.usersCount,
             this.email,
           )
            .pipe(takeWhile(() => this.isActive))
            .subscribe((data: any) => {
              if (!!data.errors && this.errorModal) {
                this.errorText = data.errors.join('; ');
                this.errorModal.show();
              }
              if (data.success) {
                if (this.changePlanModal instanceof ChangePlanModalComponent) {
                  this.changePlanModal.setUpgraded();
                }
              }
              if (this.changePlanModal instanceof ChangePlanModalComponent) {
                this.changePlanModal.hideLoader();
              }
              this.clearData();
            });
         } else { // subscription is not valid - show billing modal
           // save plan info
           this.planInfo = planInfo;
           if (this.changePlanModal instanceof ChangePlanModalComponent) {
             this.changePlanModal.hideLoader();
             this.changePlanModal.hide();
           }
           this.openStripeBillingUrl();
           this.ref.markForCheck();
         }
       }, () => {
         if (this.changePlanModal instanceof ChangePlanModalComponent) {
           this.changePlanModal.hideLoader();
         }
       });
   }

   onSubmitCardClicked(cardInfoForStripe: any) {
     if (this.manageBillingModal instanceof ManageBillingModalComponent) {
       this.manageBillingModal.showLoader();
     }

     // save email for later subscription
     this.email = cardInfoForStripe.email;
     delete cardInfoForStripe.email;

     this.createStripeToken(cardInfoForStripe)
     .pipe(takeWhile(() => this.isActive))
     .subscribe((res: any) => {
       if (typeof(res) === 'object' && _.get(res, 'error')) {
         if (this.manageBillingModal) {
           this.manageBillingModal.setError(_.get(res, 'error.message'));
           this.manageBillingModal.hideLoader();
         }
       } else if (typeof(res) === 'string') {
         const stripeToken = res;
         if (this.updateCard) {
            this.subscriptionService.updateCard(this.brandId, stripeToken)
             .pipe(takeWhile(() => this.isActive))
             .subscribe((_res: any) => {
               if (!!_res.errors && this.errorModal) {
                 this.errorText = _res.errors.join('; ');
                 this.errorModal.show();
               }

               if (this.manageBillingModal instanceof ManageBillingModalComponent) {
                 this.manageBillingModal.hideLoader();
               }
               if (!_res.error) {
                 if (this.manageBillingModal instanceof ManageBillingModalComponent) {
                   this.manageBillingModal.hide();
                 }
                 this.clearData();
                 this.subscriptionService.clearStoredAccountBrand();
                 this.cardUpdated.emit();
               }
             });
         } else {
           this.startSubscription(
             stripeToken,
             _.get(this.planInfo, 'planId') || 'plan-1',
             _.get(this.planInfo, 'usersCount'),
             this.email,
             _.get(this.couponInfo, 'id'),
           )
           .pipe(takeWhile(() => this.isActive))
           .subscribe((_res) => {
             if (!!_res.errors && this.errorModal) {
               this.errorText = _res.errors.join('; ');
               this.errorModal.show();
             }
             if (this.manageBillingModal instanceof ManageBillingModalComponent) {
               this.manageBillingModal.hideLoader();
               this.manageBillingModal.hide();
             }
             this.clearData();
             if (!this.isManageBillingFlow) {
               this.checkTerms();
             }
           });

         }
       } else {
         if (this.manageBillingModal instanceof ManageBillingModalComponent) {
           this.manageBillingModal.hideLoader();
         }
       }
     });
   }

   onUpdateCardClicked() {
     this.updateCard = true;
     if (this.billingInfoModal instanceof BillingInfoModalComponent) {
       this.billingInfoModal.hide();
     }
     if (this.manageBillingModal instanceof ManageBillingModalComponent) {
       this.manageBillingModal.show();
     }
   }

   onShareInvoiceClicked() {
     if (this.billingInfoModal instanceof BillingInfoModalComponent) {
       this.billingInfoModal.hide();
     }
     if (this.sendBillingModal instanceof SendBillingModalComponent) {
       this.sendBillingModal.show();
     }
   }

   onSendBillingSubmit(sendBillingInfo: any) {
     this.subscriptionService.postInvite(
       this.brandId, sendBillingInfo.emails,
       sendBillingInfo.ccs, sendBillingInfo.bccs,
       sendBillingInfo.subject, sendBillingInfo.body,
     )
      .subscribe((res: any) => {
        if (res.success) {
          if (this.sendBillingModal instanceof SendBillingModalComponent) {
            this.sendBillingModal.hide();
          }
          this.toastrService.info('', '', invoiceSentToast());
        }
      });
   }

   /*******************************************
    * COMPONENT PRIVATE METHODS
    *******************************************/

  private createStripeToken(cardInfoForStripe: any): Observable<any> {
    return new Observable(observer => {
      (<any>window).Stripe.card.createToken(
        cardInfoForStripe,
        (status: number, response: any) => {

         // Wrapping inside the Angular zone
         this._zone.run(() => {
           if (status === 200) {
             observer.next(response.id);
           } else {
             // response = {error: {message: '...'}}
             observer.next(response);
           }
         });
       });
     });
   }

   private startSubscription(stripeToken: string, planId?: string, maxUsersCount?: number, email?: string, coupon?: any): Observable<any> {
     const data = {
       token: stripeToken,
       planId,
       maxUsersCount,
       email,
       coupon,
     };

     return new Observable(observer => {
       this.subscriptionService.startSubscription(
         this.brandId,
         data,
       )
        .pipe(takeWhile(() => this.isActive))
        .subscribe((res) => {
          this.subscrUpdated.emit();
          observer.next(res);
        });
     });
   }

   private updateSubscription(stripeToken: string, planId?: string, maxUsersCount?: number, email?: string, coupon?: any): Observable<any> {
     const data = {
       token: stripeToken,
       planId,
       maxUsersCount,
       email,
       coupon,
     };

     return new Observable(observer => {
       this.subscriptionService.updateSubscription(
         this.brandId,
         data,
       )
        .pipe(takeWhile(() => this.isActive))
        .subscribe((res) => {
          this.subscrUpdated.emit();
          observer.next(res);
        });
     });
   }

  private setPlanId() {
    forkJoin([
      this.subscriptionService.getMonthlyPlansInfo(this.brandId),
      this.subscriptionService.getPlanItemsCount(this.brandId),
    ])
      .pipe(takeWhile(() => this.isActive))
      .subscribe((result: Array<any>) => {
        const plans = _.orderBy(result[0], (plan: any) => Number(plan.min_items));
        // @ts-ignore
        const currPlan = _.find(plans, {current_subscription: true});
        const currItemsCount = result[1]?.items;
        let planId = '';
        if (currPlan) {
          planId = currPlan.id;
        } else {
          let planIndex: number = _.findIndex(plans, (plan: any) => {
            return Number(plan.max_items) > currItemsCount;
          });
          if (planIndex < 0) {
            planIndex = plans.length - 1;
          }
          planId = plans[planIndex].id;
        }

        this.planInfo = {
          ...this.planInfo,
          planId
        };
      });
  }

   private clearData() {
     this.planInfo = null;
     this.ignoreSubscrValid = false;
     this.email = null;
     this.cardInfo = null;
     this.couponInfo = null;
     this.discountInfo = null;
     this.initialUsersCount = 0;
     this.updateCard = false;
     this.ref.markForCheck();
   }

  isValidSubscription(subscriptionRes: {
    valid: boolean;
    reason?: string;
  }): boolean {
    if (subscriptionRes?.reason === 'System user account') {
      return false;
    }
    return subscriptionRes.valid;
  }
}
